<template>
    <div class="data-by-unit-chart">
        <div
            ref="graphicParent"
            class="vis-wrapper"
        >
            <div
                :id="displayDiv"
                ref="vis"
                class="chart-display-div"
            />
        </div>
    </div>
</template>

<script>
import vegaChartHelpers from '@foundryspatial/mixin-vega-chart-helpers';

import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

export default {
    mixins: [
        vegaChartHelpers,
    ],

    props: {
        /**
         * In most cases, there will only be one data input for a chart. This input will be for a single time unit and
         * will contain all the data for the chart.
         *
         * A second input will be included when we want the time axis to be in a different unit than the data. For
         * example, if the data is per week, but we want the time axis to show the months.
         *
         * The inputs must be in the correct order in the array.
         *
         * The data is expected to be in the following format:
         *
         * [
         *      {
         *          unit: 'month',
         *          unitKey: 'm',
         *          marks: [                        // marks can be of type 'line', 'point', 'area', or 'band'
         *              {
         *                  type: 'line',
         *                  color: '#f17d43',
         *                  value: 'v',             // matches key in data points
         *              },
         *              {
         *                  type: 'point',
         *                  value: 'month_count',   // matches key in data points
         *                  secondValueScale: true,
         *                  color: '#c694c3',
         *              },
         *              {
         *                  type: 'area',
         *                  color: '#5a8ed1',
         *                  min: 'min',             // matches key in data points
         *                  max: 'max',             // matches key in data points
         *              },
         *              {
         *                  type: 'band',
         *                  value: 'annual_m3',     // matches key in data points
         *                  color: '#005400',
         *              },
         *          ],
         *          data: [
         *              {
         *                  m: 1,
         *                  v: -10.6925887299391,
         *                  min: -9.47979506920561,
         *                  max: -8.39760268426655,
         *                  annual_m3: 1919.68488,
         *                  month_count: 10,
         *              },
         *              ...
         *          ],
         *      },
         *      {
         *          timeAxisUnit: true,     // only include on input that changes time unit
         *          unit: 'month',
         *          unitKey: 'month',
         *          marks: [],
         *          data: [
         *              { month: 1 },
         *              { month: 2 },
         *              { month: 3 },
         *              { month: 4 },
         *              { month: 5 },
         *              { month: 6 },
         *              { month: 7 },
         *              { month: 8 },
         *              { month: 9 },
         *              { month: 10 },
         *              { month: 11 },
         *              { month: 12 },
         *          ],
         *      },
         * ]
         *
         * Possible mark options:
         *
         * - type                   (string)
         *      -> what type of mark this represents
         *      -> options are line, point, area, band
         *
         * - color                  (string)
         *      -> what color this mark should be
         *
         * - value                  (string)
         *      -> what data property the value is stored in
         *      -> only needed for line and point types
         *
         * - min                    (string)
         *      -> what data property the min value is stored in
         *      -> only needed for area and band types
         *      -> if not provided, the min values defaults to zero
         *
         * - max                    (string)
         *      -> what data property the max value is stored in
         *      -> only needed for area and band types
         *
         * - interpolate            (string)
         *      -> what kind of interpolation should be used
         *      -> default is 'monotone'
         *
         * - secondValueScale       (boolean)
         *      -> indicates if this mark should be associated with the second value scale
         *
         * - styling                (Object)
         *      -> any additional styling to include for the mark
         *      -> these must be options that can be included under a vega mark's update property
         *
         * - hoverStyling           (Object)
         *      -> any additional hover styling to include for the mark
         *      -> these must be options that can be included under a vega mark's update property
         *
         * - labelOrder             (number)
         *      -> the row of the tooltip that the mark should be in
         *
         */
        data: {
            type: Array,
            required: true,
        },

        /**
         * All options that can change the presentation of the chart:
         *
         * - verticalChart             (boolean, default: false)
         *      -> indicates if this a vertical chart or not
         *
         * - padValueDomain            (boolean, default: false)
         *      -> indicates if there will be "padding" added to the domain
         *      -> the max value will be multiplied by 1.15 to get the maxDomain
         *      -> if the min value is less than zero, it will be multiplied by 1.15 to get the minDomain
         *
         * - valueAxisOptions          (object, default: {})
         *      -> any options to be applied to the valueAxis
         *      -> must be vega axis configuration options
         *
         * - secondValueScale          (boolean, default: false)
         *      -> indicates if a second value scale and axis should be included in the chart
         *      -> when true on horizontal charts, the chart will have an axis along the right
         *      -> when true on vertical charts, the chart will have an axis along the top
         *
         * - secondValueAxisOptions    (object, default: {})
         *      -> any options to be applied to the secondValueAxis
         *      -> must be vega axis configuration options
         *
         * - tooltipMeasurementUnit    (string, default: '')
         *      -> the unit of measurement to be displayed after the values in the chart tooltip
         *
         * - tooltipOnWholeChart       (boolean, default: false)
         *      -> indicates if tooltip should be displayed anywhere on chart
         *      -> if false, tooltips will only be displayed on marks
         *
         * - roundToDecimalPlaces      (number, default: 2)
         *      -> number of decimals places that tooltip values should be rounded to
         *
         * - sortMarksByValue          (Object)
         *      -> whether the marks should be sorted by value
         *      -> when true, the marks with the lowest values will be in the front layer of the chart, and the marks
         *         with the highest values will be in the back layer
         */
        providedOptions: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        /**
         * The height in pixels the chart will be. If this is not provided, the chart height will be responsive and can
         * be set with styling.
         */
        height: {
            type: Number,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            tooltipSignal: null,
            dataMaxTracker: {},
            dataMinTracker: {},
            // the horizontal vega spec sets the time along the bottom axis and the values along the left axis. If
            // there is a secondValueScale, it is set to the right axis.
            specHorizontal: {
                $schema: 'https://vega.github.io/schema/vega/v5.json',
                padding: 2,
                autosize: { type: 'fit', contains: 'padding' },
                data: [],
                signals: [
                    {
                        name: 'width',
                        update: 'containerSize()[0]',
                        on: [
                            {
                                events: { source: 'window', type: 'resize', throttle: 50 },
                                update: 'containerSize()[0]',
                            },
                        ],
                    },
                    {
                        name: 'height',
                        update: 'containerSize()[1]',
                        on: [
                            {
                                events: { source: 'window', type: 'resize', throttle: 50 },
                                update: 'containerSize()[1]',
                            },
                        ],
                    },
                ],
                scales: [
                    {
                        name: 'timeScale',
                        type: 'point',
                        range: 'width',
                    },
                    {
                        name: 'timeBandScale',
                        type: 'band',
                        range: 'width',
                        padding: 0.25,
                        round: true,
                    },
                    {
                        name: 'valueScale',
                        type: 'linear',
                        range: 'height',
                        zero: true,
                        nice: true,
                        domain: { fields: [] },
                    },
                    {
                        name: 'valueScale2',
                        type: 'linear',
                        range: 'height',
                        domain: { fields: [] },
                    },
                    {
                        name: 'months',
                        type: 'linear',
                        range: 'height',
                        domain: [0, 12],
                    },
                    {
                        name: 'axisUnitScale',
                        type: 'band',
                        range: 'width',
                    },
                ],
                axes: [
                    {
                        orient: 'bottom',
                        scale: 'timeScale',
                        formatType: 'time',
                        bandPosition: 1,
                        tickExtra: true,
                        zindex: 2,
                    },
                    {
                        orient: 'left',
                        scale: 'valueScale',
                        grid: true,
                        tickCount: 10,
                        titlePadding: 10,
                        titleFontWeight: 400,
                        offset: 1,
                    },
                ],
                marks: [],
            },
            // the vertical vega spec sets the time along the left axis and the values along the bottom axis. If there
            // is a secondValueScale, it is set to the top axis.
            specVertical: {
                $schema: 'https://vega.github.io/schema/vega/v5.json',
                padding: 2,
                autosize: { type: 'fit', contains: 'padding' },
                data: [],
                signals: [
                    {
                        name: 'width',
                        update: 'containerSize()[0]',
                        on: [
                            {
                                events: { source: 'window', type: 'resize', throttle: 50 },
                                update: 'containerSize()[0]',
                            },
                        ],
                    },
                    {
                        name: 'height',
                        update: 'containerSize()[1]',
                        on: [
                            {
                                events: { source: 'window', type: 'resize', throttle: 50 },
                                update: 'containerSize()[1]',
                            },
                        ],
                    },
                ],
                scales: [
                    {
                        name: 'timeScale',
                        type: 'point',
                        range: 'height',
                    },
                    {
                        name: 'timeBandScale',
                        type: 'band',
                        range: 'height',
                        padding: 0.5,
                        round: true,
                    },
                    {
                        name: 'valueScale',
                        type: 'linear',
                        range: 'width',
                        zero: true,
                        nice: true,
                        domain: { fields: [] },
                    },
                    {
                        name: 'valueScale2',
                        type: 'linear',
                        range: 'width',
                        zero: true,
                        nice: true,
                        domain: { fields: [] },
                    },
                    {
                        name: 'months',
                        type: 'linear',
                        range: 'height',
                        domain: [0, 12],
                    },
                    {
                        name: 'axisUnitScale',
                        type: 'band',
                        range: 'height',
                    },
                ],
                axes: [
                    {
                        orient: 'left',
                        scale: 'timeScale',
                        formatType: 'time',
                        bandPosition: 1,
                        tickExtra: true,
                    },
                    {
                        orient: 'bottom',
                        scale: 'valueScale',
                        grid: true,
                        labelOverlap: true,
                        tickCount: 10,
                        titlePadding: 10,
                        titleFontWeight: 400,
                    },
                ],
                marks: [
                ],
            },
        };
    },

    computed: {

        /**
         * Change second axis scale to months when option is present
         */
        secondAxisScale() {
            return this.options.monthScale ? 'months' : 'valueScale2';
        },

        /**
         * Override default optoins with the providedOptions
         */
        options() {
            return {
                verticalChart: false,
                padValueDomain: false,
                valueScaleOptions: {},
                valueAxisOptions: {},
                secondValueScale: false,
                secondValueAxisOptions: {},
                timeAxisOptions: {},
                tooltipOnWholeChart: false,
                tooltipMeasurementUnit: '',
                roundToDecimalPlaces: 2,
                ...this.providedOptions,
            };
        },

        /**
         * Get the correct vegaSpec, depending on if this is a vertical or horizontal chart
         */
        vegaSpec() {
            if (this.vertical) {
                return this.specVertical;
            }

            return this.specHorizontal;
        },

        /**
         * Returns boolean indicating if this a vertical chart of not.
         */
        vertical() {
            return this.options.verticalChart;
        },
    },

    mounted() {
        // if there is a secondValueScale, add the axis for it
        if (this.options.secondValueScale) {
            this.vegaSpec.axes.push({
                ...this.vegaSpec.axes[1],
                orient: this.vertical ? 'top' : 'right',
                scale: this.secondAxisScale,
                grid: false,
                ...this.options.secondValueAxisOptions,
            });
        }

        this.setTooltipSignal();

        // for each data inputs, add the data and marks to the vegaSpec
        this.data.forEach((input, index) => {
            this.addData(input, index);
            this.addMarks(input, index);
        });

        // apply any axis/scale options we were given
        this.applyAxisOptions();
        this.applyScaleOptions();

        if (this.options.tooltipOnWholeChart) {
            this.addTooltipMark();
        }

        // if there is a height provided, set the chart height and remove the signal that allows for a responsive height
        if (this.height) {
            this.vegaSpec.height = this.height;
            this.vegaSpec.signals.pop();
        }

        // finally, render the chart
        this.render();
    },

    methods: {
        /**
         * Apply all options that affect the chart axes.
         */
        applyAxisOptions() {
            this.vegaSpec.axes[0] = {
                ...this.vegaSpec.axes[0],
                ...this.options.timeAxisOptions,
            };

            this.vegaSpec.axes[1] = {
                ...this.vegaSpec.axes[1],
                ...this.options.valueAxisOptions,
            };
        },

        /**
         * Apply all options that affect the chart scales
         */
        applyScaleOptions() {
            // if we want to stretch the value domain, get the min/max values and set the valueScale domain
            if (this.options.padValueDomain) {
                const minValue = Math.min(...Object.values(this.dataMinTracker));
                const maxValue = Math.max(...Object.values(this.dataMaxTracker));
                this.vegaSpec.scales[2].domain = [minValue < 0 ? minValue : 0, maxValue * 1.15];
            }

            this.vegaSpec.scales[2] = {
                ...this.vegaSpec.scales[2],
                ...this.options.valueScaleOptions,
            };
        },

        /**
         * Add a data input to the vegaSpec.
         *
         * @param {Object} input - a data input containing all information related to a particular date unit
         * @param {number} index - the index of the input
         */
        addData(input, index) {
            // add this data input to the vegaSpec
            this.vegaSpec.data.push({
                name: `data-${index}`,
                values: this.processData(input),
                transform: [
                    {
                        type: 'timeunit',
                        field: 'date',
                        units: [input.unit],
                        signal: `tbinInput${index}`,
                    },
                ],
            });

            this.makeDataBasedSpecAdjustments(input, index);
        },

        /**
         * Process the data from a data input. This includes:
         *      -> tracking the min/max values for each data property
         *      -> getting the value to be displayed in the tooltip for each data property
         *      -> converting the input time units to a date
         */
        processData(input) {
            if (!input.data.length) return [];

            // add data keys to dataMaxTracker and dataMinTracker
            const dataPoint = input.data[0];
            const dataKeys = Object.keys(dataPoint);
            dataKeys.forEach((key) => {
                if (key !== input.unitKey && typeof dataPoint[key] === 'number') {
                    this.dataMaxTracker[key] = 0;
                    this.dataMinTracker[key] = 0;
                }
            });

            // go through all data points in the input
            return input.data.map((point) => {
                const tooltipValues = {};
                // check point property values again min/max trackers and update trackers
                Object.keys(point).forEach((key) => {
                    const value = point[key];

                    if (key in this.dataMaxTracker) {
                        if (this.dataMaxTracker[key] < value) {
                            this.dataMaxTracker[key] = value;
                        }
                        if (this.dataMinTracker[key] > value) {
                            this.dataMinTracker[key] = value;
                        }
                    }

                    // get version of value to be displayed in the tooltip
                    if (key !== input.unitKey) {
                        if (typeof value === 'number') {
                            tooltipValues[`${key}Tooltip`] = this.prepareNumberForTooltip(value);
                        } else {
                            tooltipValues[`${key}Tooltip`] = value;
                        }
                    }
                });

                // return the original data point, but convert the time unit to a Date object
                return {
                    date: this.timeUnitToDate(input.unit, point[input.unitKey]),
                    displayUnit: this.timeUnitToDisplayText(input.unit, point[input.unitKey]),
                    ...point,
                    ...tooltipValues,
                };
            });
        },

        /**
         * Convert a number representing a date unit to a full Date object.
         *
         * @param {string} unit - the date unit the number represents. Can be week, year, or month.
         * @param {number} num - the number that represents a date unit
         */
        timeUnitToDate(unit, num) {
            if (unit === 'week') {
                return dayjs('2001-01-01', 'YYYY-MM-DD').week(num).toDate();
            }

            if (unit === 'year') {
                return new Date(num, 6, 1);
            }

            // if we get here, the unit should be 'month'
            return new Date(2001, num - 1, 1);
        },

        /**
         * Convert a number representing a date unit to a display string.
         *
         * @param {string} unit - the date unit the number represents. Can be week, year, or month.
         * @param {number} num - the number that represents a date unit
         */
        timeUnitToDisplayText(unit, num) {
            if (unit === 'week') {
                return `Week ${num}`;
            }

            if (unit === 'year') {
                return `${num}`;
            }

            // if we get here, the unit should be 'month'
            return dayjs().month(num - 1).format('MMMM');
        },

        /**
         * Make adjustments to vegaSpec based on the given data input. Adjustments are made to the axes and scale.
         */
        makeDataBasedSpecAdjustments(input, index) {
            // if an input represents the timeAxisUnit, we point the timeAxis at the correct scale, set the axes format
            // and set the domain of the axisUnitScale
            if (input.timeAxisUnit) {
                this.vegaSpec.axes[0].scale = 'axisUnitScale';
                this.vegaSpec.axes[0].format = { signal: `timeUnitSpecifier(tbinInput${index}.units)` };
                this.vegaSpec.scales[4].domain = { signal: `timeSequence(tbinInput${index}.unit, tbinInput${index}.start, tbinInput${index}.stop)` };
            } else {
                // if there's only one data input, we know we can format the axis for the input's time unit
                if (this.data.length === 1) {
                    this.vegaSpec.axes[0].format = { signal: `timeUnitSpecifier(tbinInput${index}.units)` };
                }

                // set the domain for the time scales
                this.vegaSpec.scales[0].domain = { signal: `timeSequence(tbinInput${index}.unit, tbinInput${index}.start, tbinInput${index}.stop)` };
                this.vegaSpec.scales[1].domain = { signal: `timeSequence(tbinInput${index}.unit, tbinInput${index}.start, tbinInput${index}.stop)` };
            }
        },

        /**
         * Add the marks associated with each data input.
         *
         * @param {Object} input - a data input containing all information related to a particular date unit
         * @param {number} index - the index of the input
         */
        addMarks(input, index) {
            const marksToAdd = [];
            input.marks.forEach((mark) => {
                if (mark.type === 'line') {
                    marksToAdd.push(this.buildLineMark(mark, index));
                } else if (mark.type === 'point') {
                    marksToAdd.push(this.buildPointMark(mark, index));
                } else if (mark.type === 'area') {
                    marksToAdd.push(this.buildAreaMark(mark, index));
                } else if (mark.type === 'band') {
                    marksToAdd.push(this.buildBandMark(mark, index));
                }
            });

            if (this.options.sortMarksByValue) {
                // sort marks based on max value and then add to vegaSpec
                const sortedMarksToAdd = marksToAdd.sort((a, b) => b.maxValue - a.maxValue);
                sortedMarksToAdd.forEach((markToAdd) => this.vegaSpec.marks.push(markToAdd.mark));
            } else {
                marksToAdd.forEach((markToAdd) => this.vegaSpec.marks.push(markToAdd.mark));
            }
        },

        /**
         * Build a line mark based on the given mark specification and return it.
         *
         * @param {Object} mark - the mark specification
         * @param {number} dataIndex - the index of the data input
         * @returns {Object} - a vegaSpec line mark
         */
        buildLineMark(mark, dataIndex) {
            // add field to domain
            if (mark.secondValueScale) {
                this.vegaSpec.scales[3].domain.fields.push({ data: `data-${dataIndex}`, field: mark.value });
            } else {
                this.vegaSpec.scales[2].domain.fields.push({ data: `data-${dataIndex}`, field: mark.value });
            }

            // create various configs that are needed
            const timeConfig = { scale: 'timeScale', field: 'unit0' };
            // set the mark's scale value appropriate, based on the mark's secondValueScale option
            const valueConfig = { scale: mark.secondValueScale ? this.secondAxisScale : 'valueScale', field: mark.value };

            return {
                maxValue: this.dataMaxTracker[mark.value],
                mark: {
                    type: 'line',
                    from: { data: `data-${dataIndex}` },
                    encode: {
                        update: {
                            // set the value/time configs along the correct axis for a vertical/horizontal chart
                            x: this.vertical ? valueConfig : timeConfig,
                            y: this.vertical ? timeConfig : valueConfig,
                            interpolate: { value: mark.interpolate || 'monotone' },
                            stroke: { value: mark.color || '#000' },
                            tooltip: {
                                signal: this.tooltipSignal,
                            },
                            ...mark.styling,
                        },
                        hover: {
                            ...mark.hoverStyling,
                        },
                    },
                },
            };
        },

        /**
         * Build a point mark based on the given mark specification and return it.
         *
         * @param {Object} mark - the mark specification
         * @param {number} dataIndex - the index of the data input
         * @returns {Object} - a vegaSpec symbol mark that is a point
         */
        buildPointMark(mark, dataIndex) {
            // add field to domain
            if (mark.secondValueScale) {
                this.vegaSpec.scales[3].domain.fields.push({ data: `data-${dataIndex}`, field: mark.value });
            } else {
                this.vegaSpec.scales[2].domain.fields.push({ data: `data-${dataIndex}`, field: mark.value });
            }

            // create various configs that are needed
            const timeConfig = { scale: 'timeScale', field: 'unit0' };
            // set the mark's scale value appropriate, based on the mark's secondValueScale option
            const valueConfig = { scale: mark.secondValueScale ? this.secondAxisScale : 'valueScale', field: mark.value };

            return {
                maxValue: this.dataMaxTracker[mark.value],
                mark: {
                    type: 'symbol',
                    from: { data: `data-${dataIndex}` },
                    encode: {
                        update: {
                            // set the value/time configs along the correct axis for a vertical/horizontal chart
                            x: this.vertical ? valueConfig : timeConfig,
                            y: this.vertical ? timeConfig : valueConfig,
                            fill: { value: mark.color },
                            strokeWidth: { value: 1 },
                            size: { value: 75 },
                            stroke: { value: mark.color || '#000' },
                            tooltip: {
                                signal: this.tooltipSignal,
                            },
                            ...mark.styling,
                        },
                        hover: {
                            size: { value: 150 },
                            ...mark.hoverStyling,
                        },
                    },
                },
            };
        },

        /**
         * Build an area mark based on the given mark specification and return it.
         *
         * @param {Object} mark - the mark specification
         * @param {number} dataIndex - the index of the data input
         * @returns {Object} - a vegaSpec area mark
         */
        buildAreaMark(mark, dataIndex) {
            // add fields to domain
            if (mark.min) {
                this.vegaSpec.scales[2].domain.fields.push({ data: `data-${dataIndex}`, field: mark.min });
            }
            this.vegaSpec.scales[2].domain.fields.push({ data: `data-${dataIndex}`, field: mark.max });

            // create various configs that are needed
            const timeConfig = { scale: 'timeScale', field: 'unit0' };
            // if there is no min value, set min to 0
            const valueConfig = mark.min ? { scale: 'valueScale', field: mark.min } : { scale: 'valueScale', value: 0 };
            const valueConfig2 = { scale: 'valueScale', field: mark.max };

            return {
                maxValue: this.dataMaxTracker[mark.max],
                mark: {
                    type: 'area',
                    from: { data: `data-${dataIndex}` },
                    encode: {
                        update: {
                            // set the value/time configs along the correct axis for a vertical/horizontal chart
                            x: this.vertical ? valueConfig : timeConfig,
                            x2: this.vertical ? valueConfig2 : null,
                            y: this.vertical ? timeConfig : valueConfig,
                            y2: this.vertical ? null : valueConfig2,
                            interpolate: { value: mark.interpolate || 'monotone' },
                            fill: { value: mark.color || '#000' },
                            orient: this.vertical ? { value: 'horizontal' } : { value: 'vertical' },
                            tooltip: {
                                signal: this.tooltipSignal,
                            },
                            ...mark.styling,
                        },
                        hover: {
                            ...mark.hoverStyling,
                        },
                    },
                },
            };
        },

        /**
         * Build a band mark based on the given mark specification and return it.
         *
         * @param {Object} mark - the mark specification
         * @param {number} dataIndex - the index of the data input
         * @returns {Object} - a vegaSpec band mark
         */
        buildBandMark(mark, dataIndex) {
            // make the line scale match up with the band scale
            this.vegaSpec.axes[0].scale = 'timeBandScale';
            this.vegaSpec.axes[0].ticks = false;
            this.vegaSpec.axes[0].labelPadding = 10;

            if (this.options.alignScales) {
                this.vegaSpec.scales[0].padding = 0.65;
            }

            // add field to domain
            this.vegaSpec.scales[2].domain.fields.push({ data: `data-${dataIndex}`, field: mark.max });

            // create various configs that are needed
            const timeConfig = { scale: 'timeBandScale', field: 'unit0' };
            const valueConfig = { scale: 'valueScale', field: mark.max };
            const valueConfig2 = mark.min ? { scale: 'valueScale', field: mark.min } : { scale: 'valueScale', value: 0 }; // set lower value coordinate to 0
            const bandConfig = { scale: 'timeBandScale', band: 1 };

            return {
                maxValue: this.dataMaxTracker[mark.max],
                mark: {
                    type: 'rect',
                    from: { data: `data-${dataIndex}` },
                    encode: {
                        update: {
                            // set the value/time configs along the correct axis for a vertical/horizontal chart
                            x: this.vertical ? valueConfig : timeConfig,
                            x2: this.vertical ? valueConfig2 : null,
                            y: this.vertical ? timeConfig : valueConfig,
                            y2: this.vertical ? null : valueConfig2,
                            // set the band config along the correct dimension for a vertical/horizontal chart
                            width: this.vertical ? null : bandConfig,
                            height: this.vertical ? bandConfig : null,
                            fill: { value: mark.color || '#000' },
                            tooltip: {
                                signal: this.tooltipSignal,
                            },
                            ...mark.styling,
                        },
                        hover: {
                            ...mark.hoverStyling,
                        },
                    },
                },
            };
        },

        /**
         * Add mark that covers the entire chart for the tooltip to be attached to.
         *
         * TODO: make this work for vertical charts and band/bar charts
         */
        addTooltipMark() {
            this.vegaSpec.marks.splice(0, 0, {
                type: 'area',
                from: { data: 'data-0' },
                encode: {
                    update: {
                        x: this.vertical ? null : { scale: 'timeScale', field: 'unit0' },
                        y: this.vertical ? { scale: 'timeScale', field: 'unit0' } : null,
                        height: { field: { group: 'height' } },
                        stroke: { value: 'transparent' },
                        fill: { value: 'transparent' },
                        tooltip: {
                            signal: this.tooltipSignal,
                        },
                    },
                },
            });
        },

        /**
         * Get and set the tooltip signal
         */
        setTooltipSignal() {
            const tooltipSections = this.getTooltipSections();
            this.tooltipSignal = `{title: datum.displayUnit, ${tooltipSections.join(', ')}}`;
        },

        /**
         * Build all tooltip sections and return them.
         *
         * @returns {Array} - strings of tooltip sections
         */
        getTooltipSections() {
            const { marks } = this.data[0];
            const sortedMarks = [...marks].sort((a, b) => a.labelOrder - b.labelOrder);

            const tooltipSections = [];
            sortedMarks.forEach((mark) => {
                if (mark.type === 'area') {
                    if (mark.min) {
                        const minDatumKey = `datum.${mark.min}Tooltip`;
                        tooltipSections.push(
                            `"${mark.minName}": ${minDatumKey} || ${minDatumKey} === 0 ? ${minDatumKey} + "${this.options.tooltipMeasurementUnit}" : "N/A"`,
                        );
                    }
                    if (mark.max) {
                        const maxDatumKey = `datum.${mark.max}Tooltip`;
                        tooltipSections.push(
                            `"${mark.maxName}": ${maxDatumKey} || ${maxDatumKey} === 0 ? ${maxDatumKey} + "${this.options.tooltipMeasurementUnit}" : "N/A"`,
                        );
                    }
                } else {
                    let key;
                    let datumKey;
                    if (mark.value && mark.valueName) {
                        key = mark.valueName;
                        datumKey = mark.value;
                    }

                    if (mark.max && mark.maxName) {
                        key = mark.maxName;
                        datumKey = mark.max;
                    }

                    if (mark.min && mark.minName) {
                        key = mark.minName;
                        datumKey = mark.min;
                    }

                    if (mark.tooltipValue && mark.tooltipName) {
                        key = mark.tooltipName;
                        datumKey = mark.tooltipValue;
                    }

                    if (key && datumKey) {
                        datumKey = `datum.${datumKey}Tooltip`;

                        tooltipSections.push(
                            `"${key}": ${datumKey} || ${datumKey} === 0 ? ${datumKey} + "${this.options.tooltipMeasurementUnit}" : "N/A"`,
                        );
                    }
                }
            });

            return tooltipSections;
        },
    },
};
</script>

<style lang="scss">
.data-by-unit-chart {
    $color-chart-axis-grid: #ddd !default;
    $color-chart-axis-labels: #000 !default;
    $color-chart-axis-domain: #888 !default;
    $family-primary: 'Lato', sans-serif !default;

    g.role-axis {
        .role-axis-domain,
        .role-axis-tick {
            line {
                stroke: $color-chart-axis-domain;
            }
        }

        .role-axis-label,
        .role-axis-title {
            text {
                fill: $color-chart-axis-labels;
                font-family: $family-primary;
            }
        }

        .role-axis-grid {
            line {
                stroke: $color-chart-axis-grid;
            }
        }
    }

    .vis-wrapper {
        overflow: hidden;
        width: 100%;
        height: 100%;

        .chart-display-div {
            height: 100%;
            width: 100%;
        }
    }
}
</style>
